import {
  Download,
  Layers,
  LayoutGrid,
  List as ListIcon,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  REVIEW_STATUSES,
  reviewStatusLabel,
  reviewStatusVariant,
} from '@/features/hrm/reviews/constants';
import type { PerformanceReview } from '@/features/hrm/reviews';
import { REVIEWS_BASE } from '../api';
import { CabinetEmptyState } from '../components/listChrome';
import { useCabinetReviews } from '../queries';
import { useReviewsUi, type ReviewsViewMode } from '../store/reviewsViewPrefs';

type ReviewOrderingColumn = 'period' | 'status' | 'rating' | 'review_date';

const VIEW_MODES: readonly ViewModeOption<ReviewsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

export function CabinetReviewsPage() {
  const navigate = useNavigate();
  const open = (r: PerformanceReview) => navigate(`/me/reviews/${r.public_id}`);
  const view = useReviewsUi((s) => s.view);
  const setView = useReviewsUi((s) => s.setView);
  const ordering = useReviewsUi((s) => s.ordering);
  const setOrdering = useReviewsUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCabinetReviews({
    page,
    search: search || undefined,
    ordering,
  });
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Performance reviews</h1>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => {
              setPage(1);
              setSearchInput(e.target.value);
            }}
            placeholder="Search period or summary…"
            className="pl-8"
            aria-label="Search reviews"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              void exportCsv(REVIEWS_BASE, {
                search: search || undefined,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export my reviews as CSV"
            title="Export my reviews as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : view === 'table' ? (
        <ReviewsTable rows={rows} ordering={ordering} onOrderingChange={setOrdering} onOpen={open} />
      ) : view === 'list' ? (
        <ReviewsList rows={rows} onOpen={open} />
      ) : view === 'cards' ? (
        <ReviewsCards rows={rows} onOpen={open} />
      ) : (
        <ReviewsGroupedByStatus rows={rows} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} review{data.count === 1 ? '' : 's'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

function ReviewsTable({
  rows,
  ordering,
  onOrderingChange,
  onOpen,
}: {
  rows: PerformanceReview[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onOpen: (r: PerformanceReview) => void;
}) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<ReviewOrderingColumn>
              column="period"
              value={ordering}
              onChange={onOrderingChange}
            >
              Period
            </SortableTableHead>
            <SortableTableHead<ReviewOrderingColumn>
              column="review_date"
              value={ordering}
              onChange={onOrderingChange}
            >
              Reviewed
            </SortableTableHead>
            <SortableTableHead<ReviewOrderingColumn>
              column="rating"
              value={ordering}
              onChange={onOrderingChange}
              className="text-right"
            >
              Rating
            </SortableTableHead>
            <SortableTableHead<ReviewOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No reviews assigned to you yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => onOpen(r)}>
                <TableCell className="font-medium">{r.period || '—'}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(r.review_date)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.rating != null ? `${r.rating}/5` : '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={reviewStatusVariant(r.status)}>
                    {reviewStatusLabel(r.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-right text-xs">
                  {r.action_items.length
                    ? `${r.action_items.filter((a) => a.is_done).length}/${r.action_items.length} items`
                    : '—'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ReviewsList({
  rows,
  onOpen,
}: {
  rows: PerformanceReview[];
  onOpen: (r: PerformanceReview) => void;
}) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            onClick={() => onOpen(r)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-semibold">{r.period || 'Review'}</span>
            {r.rating != null ? (
              <span className="text-muted-foreground tabular-nums">{r.rating}/5</span>
            ) : null}
            <span className="text-muted-foreground hidden sm:inline">{fmtDate(r.review_date)}</span>
            <Badge variant={reviewStatusVariant(r.status)} className="shrink-0">
              {reviewStatusLabel(r.status)}
            </Badge>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ReviewsCards({
  rows,
  onOpen,
}: {
  rows: PerformanceReview[];
  onOpen: (r: PerformanceReview) => void;
}) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <Card
          key={r.id}
          role="button"
          tabIndex={0}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
          onClick={() => onOpen(r)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(r);
            }
          }}
        >
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
              <span>{r.period || 'Review'}</span>
              <span className="flex items-center gap-2">
                {r.rating != null ? (
                  <span className="text-muted-foreground text-sm">Rating: {r.rating}/5</span>
                ) : null}
                <Badge variant={reviewStatusVariant(r.status)}>{reviewStatusLabel(r.status)}</Badge>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">Reviewed {fmtDate(r.review_date)}</p>
            {r.summary ? <p className="whitespace-pre-line">{r.summary}</p> : null}
            {r.action_items.length > 0 ? (
              <div>
                <p className="text-xs font-medium uppercase">Action items</p>
                <ul className="mt-1 space-y-1">
                  {r.action_items.map((a) => (
                    <li key={a.id} className="flex items-center gap-2">
                      <span
                        className={
                          a.is_done ? 'text-muted-foreground line-through' : 'text-foreground'
                        }
                      >
                        {a.title}
                      </span>
                      {a.is_done ? <Badge variant="secondary">Done</Badge> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ReviewsGroupedByStatus({
  rows,
  onOpen,
}: {
  rows: PerformanceReview[];
  onOpen: (r: PerformanceReview) => void;
}) {
  const groups = useMemo(
    () =>
      REVIEW_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((r) => r.status === s.value),
      })).filter((g) => g.bucket.length > 0),
    [rows],
  );

  if (rows.length === 0) return <EmptyText />;

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key} aria-labelledby={`reviews-status-${key}`}>
          <h2
            id={`reviews-status-${key}`}
            className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
          >
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <ReviewsCards rows={bucket} onOpen={onOpen} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>No reviews assigned to you yet.</CabinetEmptyState>;
}
